import {
  Box,
  Divider,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikErrors, FormikValues } from "formik";
import { useState, useCallback, useMemo, memo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { userRoles } from "../../../../constants/user";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { setLoadingAction } from "../../../../redux/utils/actions";
import patientService from "../../../../services/patient";
import { logger } from "../../../../utils/logger";
import RoutingList from "../../../../utils/RoutingList";
import FormStepBottomComponent from "../../../common/form/Steps/FormStepBottomComponent";
import FormStepNavComponent from "../../../common/form/Steps/FormStepComponent";
import FormStepTitle from "../../../common/form/Steps/FormStepTitle";
import StepsHeadingSection from "../../../common/form/Steps/StepsHeadingSection";
import styles from "../styles/styles.module.scss";
import {
  referralCreationStepsInfo,
  referralInitData,
  referralValidationSchema,
} from "../utils";
import AddReReferralForm from "./AddReferralForm";
import ReferralSearchDropdown from "./ReferralSearchDropdown";
import PatientAdmissionTitleAndInput from "../../patient/AddPatient/form/PatientAdmissionTitleAndInput";
import InputField from "../../../common/form/input/InputField";
import { H5 } from "../../patient/styles";

// Financial summary component
const FinancialSummary = memo(({ patient }: { patient: any }) => {
  // Format currency with Indian locale
  const formatCurrency = useCallback((amount: number = 0) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  }, []);

  // Calculate remaining amount
  const remainingAmount = useMemo(() => {
    return ((patient?.billAmount || 0) - (patient?.maxAmount || 0));
  }, [patient?.billAmount, patient?.maxAmount]);

  if (!patient) return null;

  return (
    <Box sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "4px",
      p: 2,
      mb: 2,
      mt: 2,
      backgroundColor: "#fff",
    }}>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1.5,
          fontWeight: 500,
          fontSize: "16px",
          color: "#242424",
        }}
      >
        Financial Summary
      </Typography>
      <Box sx={{ width: "100%" }}>
        {/* Approved Amount Row */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          borderBottom: "1px solid #f5f5f5",
          pb: 0.5,
        }}>
          <Typography variant="body2" sx={{ color: "#666", fontSize: "14px" }}>
            Approved Amount
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "#1976d2", fontSize: "14px" }}>
            {formatCurrency(patient?.maxAmount)}
          </Typography>
        </Box>
        
        {/* Bill Amount Row */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          borderBottom: "1px solid #f5f5f5",
          pb: 0.5,
        }}>
          <Typography variant="body2" sx={{ color: "#666", fontSize: "14px" }}>
            Bill Amount
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
            {formatCurrency(patient?.billAmount)}
          </Typography>
        </Box>
        
        {/* Remaining Amount Row */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          pb: 0.5,
        }}>
          <Typography variant="body2" sx={{ color: "#666", fontSize: "14px" }}>
            Remaining Amount
          </Typography>
          <Typography variant="body2" sx={{
            fontWeight: 500,
            color: remainingAmount > 0 ? "#d32f2f" : "#2e7d32",
            fontSize: "14px",
          }}>
            {remainingAmount > 0 ? "-" : ""} {" "}
            {formatCurrency(Math.abs(remainingAmount))}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

const AddReferral = () => {
  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [steps, setSteps] = useState(0);
  const stepsInfo = useMemo(() => referralCreationStepsInfo, []);

  // Auth state
  const { hasReReferPermission, role } = useAppSelector(
    (state) => state.auth.profileData.user
  );

  /**
   * Handle patient selection by fetching patient details
   */
  const handlePatientSelect = useCallback(async (patientId: string) => {
    try {
      dispatch(setLoadingAction(true));
      const response = await patientService.getByIdForReferral(patientId);
      setSelectedPatient(response?.data?.data);
      dispatch(setLoadingAction(false));
    } catch (error) {
      dispatch(setLoadingAction(false));
      console.error("Error fetching patient details:", error);
      toast.error("Failed to load patient details");
    }
  }, [dispatch]);

  /**
   * Save referral data to backend
   */
  const saveHandler = useCallback(async (values: FormikValues) => {
    try {
      dispatch(setLoadingAction(true));
      const formValues = {
        patientId: values.patientId,
        remark: values.remark,
        referralDocuments: values.referralDocuments,
      };
      await patientService.addReferral(formValues);
      dispatch(setLoadingAction(false));
      toast.success("Referral saved successfully");
      navigate(RoutingList.patientAdmission.index);
    } catch (error: any) {
      dispatch(setLoadingAction(false));
      toast.error(error?.response?.data?.msg || "Something went wrong");
    }
  }, [dispatch, navigate]);

  /**
   * Handle form step navigation with validation
   */
  const stepHandler = useCallback((
    validateForm: (values: FormikValues) => Promise<any>,
    values: FormikValues,
    stepIndex: number,
    setErrors?: (errors: FormikErrors<FormikValues>) => void,
    setFieldTouched?: (
      field: string,
      isTouched?: boolean,
      shouldValidate?: boolean
    ) => void
  ) => {
    // Validate form and handle step navigation
    validateForm(values).then((errors) => {
      if (Object.entries(errors).length === 0) {
        // No validation errors - proceed
        const lastStepIndex = referralCreationStepsInfo.length - 1;
        
        if (stepIndex <= -1) {
          // Go to first step
          setSteps(0);
        } else if (steps === lastStepIndex && stepIndex > lastStepIndex) {
          // Submit form on last step
          saveHandler(values);
        } else if (stepIndex <= lastStepIndex) {
          // Navigate to specified step
          setSteps(stepIndex);
        }
      } else {
        // Show validation errors
        logger.error(errors);
        if (setErrors) {
          Object.keys(errors).forEach((key) => {
            setFieldTouched?.(key, true);
          });
          setErrors(errors);
        }
      }
    });
  }, [selectedPatient, steps, saveHandler]);

  /**
   * Handle patient search selection
   */
  const handlePatientSearchSelect = useCallback((patientId: string, resetForm: Function) => {
    handlePatientSelect(patientId);
    setSteps(0);
    resetForm({
      values: {
        ...referralInitData,
        patientId: patientId,
      },
    });
  }, [handlePatientSelect]);

  // Check permissions
  if (!hasReReferPermission && role !== userRoles.SUPER_ADMIN) {
    return <Navigate to={RoutingList.patientAdmission.index} />;
  }

  return (
    <Box className={styles.boxPadding}>
      <Formik
        initialValues={referralInitData}
        validationSchema={referralValidationSchema[steps]}
        onSubmit={(values, { validateForm, setErrors, setFieldTouched }) => {
          stepHandler(
            validateForm,
            values,
            steps > stepsInfo.length - 1 ? steps : steps + 1,
            setErrors,
            setFieldTouched
          );
        }}
      >
        {({ values, resetForm }) => (
          <Form>
            {/* Page header */}
            <StepsHeadingSection
              title="Referral"
              subtitle="Admit Patient using valid referral"
              isEdit={false}
            />
            
            {/* Step navigation */}
            <FormStepNavComponent
              steps={steps}
              setSteps={setSteps}
              stepHandler={stepHandler}
              stepsInfo={stepsInfo}
            />

            {/* Main content */}
            <Grid container sx={{ justifyContent: "center" }}>
              <Grid item xs={12} lg={8}>
                <FormStepTitle title="Referral Patient Details" required />
                <Box sx={{ color: "#242424" }}>
                  {/* Patient search */}
                  <ReferralSearchDropdown
                    title="Search Patient*"
                    onPatientSelect={(patientId) => handlePatientSearchSelect(patientId, resetForm)}
                  />
                  
                  {/* Financial summary */}
                  {selectedPatient && <FinancialSummary patient={selectedPatient} />}
                  
                  {/* Referral form */}
                  {values.patientId && (
                    <AddReReferralForm
                      steps={steps}
                      selectedPatientId={selectedPatient?._id || ""}
                    />
                  )}
                  
                  <Divider className={styles.mTopBottom20} />

                  {/* Step navigation buttons */}
                  <FormStepBottomComponent
                    steps={steps}
                    setSteps={setSteps}
                    stepsInfo={stepsInfo}
                    stepHandler={stepHandler}
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddReferral;
